import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode
} from 'react';
import type { CartItem } from './CartProvider';

/** Cancellation state of a placed line: requested (awaiting waiter/admin) → cancelled (approved). */
export type CancelStatus = 'requested' | 'cancelled';

/** A single line within an order. */
export type OrderLine = CartItem & { cancelStatus?: CancelStatus };

/** How the order is fulfilled: dine-in / takeaway / packed parcel / home delivery. */
export type OrderType = 'dine-in' | 'takeaway' | 'packed' | 'delivery';

/** Kitchen progress for one placed order. */
export type OrderStatus = 'placed' | 'preparing' | 'ready' | 'served';

/**
 * One placed order (a "batch"). Every time the customer taps Place order /
 * Add to order a new batch is created, so each shows its own status in the
 * tracking screen.
 */
export interface OrderBatch {
  id: string;
  at: string;
  type: OrderType;
  status: OrderStatus;
  items: OrderLine[];
}

interface OrderSessionValue {
  getBatches: (tableCode: string) => OrderBatch[];
  /** All batches' lines merged by item (for the running bill summary). */
  getItems: (tableCode: string) => OrderLine[];
  getTotal: (tableCode: string) => number;
  getCount: (tableCode: string) => number;
  /** Record a new placed order for a table and return it. */
  placeOrder: (tableCode: string, items: OrderLine[], type: OrderType) => void;
  clearOrder: (tableCode: string) => void;
  /** Customer raises a cancellation request for a placed line (awaits waiter/admin). */
  requestCancel: (tableCode: string, batchId: string, lineId: number) => void;
  /** Waiter/admin approves the cancellation — the line drops out of the bill. */
  approveCancel: (tableCode: string, batchId: string, lineId: number) => void;
  /** All tables merged with this one (incl. itself). Admin action, treated as one bill. */
  getGroupTables: (tableCode: string) => string[];
  /** Admin merges `other` into `tableCode`'s group so their orders bill as one. */
  mergeTable: (tableCode: string, other: string) => void;
  /** Admin splits the merge group containing `tableCode` back into separate tables. */
  unmergeGroup: (tableCode: string) => void;
}

type BatchMap = Record<string, OrderBatch[]>;
/** Merge groups keyed by the primary table → member tables (incl. primary). */
type GroupMap = Record<string, string[]>;
/** Per-user storage keys so one customer's orders/bill never leak to the next. */
const ordersKey = (userId: string | number | null) => `thalios.orders.v2.${userId ?? 'guest'}`;
const groupsKey = (userId: string | number | null) => `thalios.tablegroups.${userId ?? 'guest'}`;
const ORDER_BASE = 4821;

function readOrders(userId: string | number | null): BatchMap {
  try {
    const raw = window.localStorage.getItem(ordersKey(userId));
    return raw ? (JSON.parse(raw) as BatchMap) : {};
  } catch {
    return {};
  }
}
function readGroups(userId: string | number | null): GroupMap {
  try {
    const g = window.localStorage.getItem(groupsKey(userId));
    return g ? (JSON.parse(g) as GroupMap) : {};
  } catch {
    return {};
  }
}

const OrderSessionContext = createContext<OrderSessionValue | null>(null);

function mergeLines(lines: OrderLine[]): OrderLine[] {
  const out: OrderLine[] = [];
  for (const l of lines) {
    const existing = out.find((o) => o.id === l.id);
    if (existing) existing.qty += l.qty;
    else out.push({ ...l });
  }
  return out;
}

/**
 * Tracks the running dine-in orders per table (each placement is its own
 * batch with a type + status), persisted to localStorage — this is what lets
 * a customer add more items to an existing table bill and see every order's
 * status separately.
 */
export function OrderSessionProvider({
  children,
  userId = null
}: {
  children: ReactNode;
  userId?: string | number | null;
}) {
  // Lazy-init from the current customer's store so mount never writes an empty {}
  // over saved orders. `keyRef` tracks which customer the state belongs to.
  const keyRef = useRef<string | number | null>(userId);
  const [batchMap, setBatchMap] = useState<BatchMap>(() => readOrders(userId));
  const [groups, setGroups] = useState<GroupMap>(() => readGroups(userId));

  // Reload only when the customer actually CHANGES (mount is already lazy-initialized),
  // so a new customer never sees the previous one's orders/bill.
  useEffect(() => {
    if (keyRef.current === userId) return; // initial mount — state already loaded
    keyRef.current = userId;
    setBatchMap(readOrders(userId));
    setGroups(readGroups(userId));
  }, [userId]);

  // Persist on data change to the customer the state belongs to (keyRef, not userId,
  // so a switch never flushes stale data to the new key).
  useEffect(() => {
    try {
      window.localStorage.setItem(ordersKey(keyRef.current), JSON.stringify(batchMap));
    } catch {
      /* ignore */
    }
  }, [batchMap]);

  useEffect(() => {
    try {
      window.localStorage.setItem(groupsKey(keyRef.current), JSON.stringify(groups));
    } catch {
      /* ignore */
    }
  }, [groups]);

  const placeOrder = useCallback((tableCode: string, items: OrderLine[], type: OrderType) => {
    if (items.length === 0) return;
    setBatchMap((prev) => {
      const existing = prev[tableCode] ?? [];
      const batch: OrderBatch = {
        id: `#${ORDER_BASE + existing.length}`,
        at: new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }),
        type,
        status: 'preparing',
        items: items.map((i) => ({ ...i }))
      };
      return { ...prev, [tableCode]: [...existing, batch] };
    });
  }, []);

  const clearOrder = useCallback((tableCode: string) => {
    setBatchMap((prev) => {
      const next = { ...prev };
      delete next[tableCode];
      return next;
    });
  }, []);

  const setLineStatus = useCallback(
    (tableCode: string, batchId: string, lineId: number, status: CancelStatus) => {
      setBatchMap((prev) => {
        const list = prev[tableCode] ?? [];
        return {
          ...prev,
          [tableCode]: list.map((b) =>
            b.id === batchId
              ? {
                  ...b,
                  items: b.items.map((l) =>
                    l.id === lineId ? { ...l, cancelStatus: status } : l
                  )
                }
              : b
          )
        };
      });
    },
    []
  );

  const requestCancel = useCallback(
    (t: string, b: string, l: number) => setLineStatus(t, b, l, 'requested'),
    [setLineStatus]
  );
  const approveCancel = useCallback(
    (t: string, b: string, l: number) => setLineStatus(t, b, l, 'cancelled'),
    [setLineStatus]
  );

  const getGroupTables = useCallback(
    (t: string): string[] => {
      for (const members of Object.values(groups)) {
        if (members.includes(t)) return members;
      }
      return [t];
    },
    [groups]
  );

  const mergeTable = useCallback((primary: string, other: string) => {
    if (primary === other) return;
    setGroups((prev) => {
      // Resolve the group `primary` already belongs to (as key or member).
      let key = primary;
      let members = prev[primary];
      if (!members) {
        for (const [k, m] of Object.entries(prev)) {
          if (m.includes(primary)) {
            key = k;
            members = m;
            break;
          }
        }
      }
      members = members ?? [primary];
      if (members.includes(other)) return prev;
      // Detach `other` from any group it was previously in.
      const next: GroupMap = {};
      for (const [k, m] of Object.entries(prev)) {
        if (k === key) continue;
        const filtered = m.filter((x) => x !== other);
        if (filtered.length > 1) next[k] = filtered;
      }
      next[key] = [...members.filter((x) => x !== other), other];
      return next;
    });
  }, []);

  const unmergeGroup = useCallback((t: string) => {
    setGroups((prev) => {
      const next: GroupMap = {};
      for (const [k, m] of Object.entries(prev)) {
        if (m.includes(t)) continue; // drop the group containing t
        next[k] = m;
      }
      return next;
    });
  }, []);

  const value = useMemo<OrderSessionValue>(() => {
    // Approved-cancelled lines are excluded from the bill totals.
    const linesFor = (t: string) =>
      mergeLines(
        (batchMap[t] ?? []).flatMap((b) => b.items).filter((l) => l.cancelStatus !== 'cancelled')
      );
    return {
      getBatches: (t) => batchMap[t] ?? [],
      getItems: linesFor,
      getTotal: (t) => linesFor(t).reduce((s, i) => s + i.qty * i.price, 0),
      getCount: (t) => linesFor(t).reduce((n, i) => n + i.qty, 0),
      placeOrder,
      clearOrder,
      requestCancel,
      approveCancel,
      getGroupTables,
      mergeTable,
      unmergeGroup
    };
  }, [
    batchMap,
    placeOrder,
    clearOrder,
    requestCancel,
    approveCancel,
    getGroupTables,
    mergeTable,
    unmergeGroup
  ]);

  return <OrderSessionContext.Provider value={value}>{children}</OrderSessionContext.Provider>;
}

export function useOrderSession(): OrderSessionValue {
  const ctx = useContext(OrderSessionContext);
  if (!ctx) throw new Error('useOrderSession must be used within an <OrderSessionProvider>');
  return ctx;
}
